import widgetTemplate from './rf-widget.html';
import widgetCSS from '!!raw-loader!./rf-widget.css';

import stepIntro1 from './templates/intro-1.html';
import stepIntro2 from './templates/intro-2.html';
import stepIntro3 from './templates/intro-3.html';
import stepIntro4 from './templates/intro-4.html';

import stepBuy1 from './templates/buy-1.html';
import stepBuy2 from './templates/buy-2.html';
import stepBuy3 from './templates/buy-3.html';
import stepBuyUp from './templates/buy-up.html';

import stepSell1 from './templates/sell-1.html';
import stepSellUp from './templates/sell-up.html';

import submission from './templates/submission.html';
import chooseTrack from './templates/choose-track.html';

import errorTemplate from '../shared/error.html';

import * as helpers from '../shared/helpers';
import { bootstrapStyle } from '../shared/styles';
import { library, dom } from '@fortawesome/fontawesome-svg-core'
import { all } from '@awesome.me/kit-bd5fe14630/icons'

const combinedStyles = `${widgetCSS}\n${bootstrapStyle}`
// Currency formatter (US example)
const moneyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const TEMPLATES = {
    'intro-1': stepIntro1,
    'intro-2': stepIntro2,
    'intro-3': stepIntro3,
    'intro-4': stepIntro4,

    'buy-1': stepBuy1,
    'buy-2': stepBuy2,
    'buy-3': stepBuy3,
    'buy-up': stepBuyUp,

    'sell-1': stepSell1,
    'sell-up': stepSellUp,

    'choose-track': chooseTrack
};

const INTRO_STEPS = ['intro-1', 'intro-2', 'intro-3', 'intro-4']
const BUY_STEPS = ['buy-1', 'buy-2', 'buy-3']
const SELL_STEPS = ['sell-1']

class RegistrationFormWidget extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        const style = document.createElement('style');
        style.textContent = combinedStyles;
        this.shadowRoot.appendChild(style);

        const template = document.createElement('section');
        template.classList.add('ha-widget');
        template.innerHTML = widgetTemplate;
        this.shadowRoot.appendChild(template);

        library.add(...all);
        dom.watch({
            autoReplaceSvgRoot: this.shadowRoot,
            observeMutationsRoot: this.shadowRoot
        });
    }

    connectedCallback() {
        this.baseUrl = helpers.getBaseUrl();
        this.baseApiUrl = helpers.getBaseApiUrl();
        this.fontColor = this.getAttribute('font-color') || '#333333';
        this.primaryColor = this.getAttribute('primary-color') || '#024267';
        this.secondaryColor = this.getAttribute('secondary-color') || '#F79322';
        this.widgetBackground = this.getAttribute('background-color') || 'transparent';
        this.fontFamily = this.getAttribute('font-family') || '"Helvetica Neue", Helvetica, Arial, sans-serif'
        this.formTitle = this.getAttribute('form-title') || 'Let\'s get you started on your journey';
        this.fontUrl = this.getAttribute('font-url') || '';
        this.errorPhone = this.getAttribute('error-phone') || null;
        this.errorEmail = this.getAttribute('error-email') || 'service@homeadvantage.com';

        this.formType = this.getAttribute('type') || '';
        this.upsell = this.getAttribute('upsell') || 'yes';
        this.offerId = this.getAttribute('offer') || '';
        this.stepQueue = [];
        this.stepIndex = 0;
        this.state = {};

        this.init();
    }

    init() {
        helpers.setColors(this);
        this.quizContainer = this.shadowRoot.querySelector('.quiz-container');
        helpers.attachPhoneMask(this, { selectorOrName: 'phone' });

        this.initQueue();
        this.bindDataCapture();
        this.bindAutocompleteEventsOnce();
        this.renderForm()
        const title = this.shadowRoot.querySelector('.form-title');
        title.innerHTML = this.formTitle;
    }

    initQueue() {
        this.stepQueue.push(...INTRO_STEPS);
        this.setQueueOrder(this.formType);
    }

    setQueueOrder(oppType) {
        if (oppType === 'buy') {
            this.stepQueue.push(...BUY_STEPS);
            this.state['buySellId'] = 1;
            if (this.upsell === 'yes' && this.formType === "buy") {
                this.stepQueue.push('sell-up');
            }
        }
        else if (oppType === 'sell') {
            this.stepQueue.push(...SELL_STEPS);
            this.state['buySellId'] = 2;
            if (this.upsell === 'yes' && this.formType === "sell") {
                this.stepQueue.push('buy-up');
            }
        }
        else {
            this.stepQueue.push('choose-track');
        }
    }

    bindDataCapture() {
        if (this._navHandler) {
            this.quizContainer.removeEventListener('click', this._navHandler);
        }
        if (this._trackHandler) {
            this.quizContainer.removeEventListener('click', this._valueHandler);
        }
        if (this._submitHandler) {
            this.quizContainer.removeEventListener('submit', this._submitHandler);
        }

        this._navHandler = (e) => {
            const btn = e.target.closest('.btn-next, .btn-prev');
            if (!btn || !this.quizContainer.contains(btn)) return;

            e.preventDefault();

            if (btn.classList.contains('btn-next')) {
                const form = btn.closest('form');
                if (form) {
                    form.requestSubmit ? form.requestSubmit() : form.submit();
                    return;
                }

                this.mergeStateFromContainer(this.quizContainer);
                this.nextStep();
            } else {
                this.prevStep();
            }
        };

        this._valueHandler = (e) => {
            const btn = e.target.closest('button[data-value]');
            if (!btn) return;

            const form = btn.closest('form');
            if (!form) return;

            const choice = btn.getAttribute('data-value');
            const targetName = btn.getAttribute('data-target'); // which hidden to set
            if (!targetName) return;

            if (targetName === 'upSell') {
                if (choice === 'buy') {
                    this.stepQueue.push(...BUY_STEPS);
                }
                else if (choice === 'sell') {
                    this.stepQueue.push(...SELL_STEPS);
                }
                else {
                    this.submit();
                    return;
                }
                this.state['buySellId'] = 0;
            }
            else {
                const hidden = form.querySelector(`input[name="${targetName}"]`);
                if (!hidden) return;

                hidden.value = choice;

                if (targetName === 'buySellId') {
                    const wantBuy = (choice === '1' || choice === '0');
                    const wantSell = (choice === '2' || choice === '0');
                    const hasBuy = this.stepQueue.includes('buy-1');
                    const hasSell = this.stepQueue.includes('sell-1');
                    if (wantBuy && !hasBuy) this.setQueueOrder('buy');
                    if (wantSell && !hasSell) this.setQueueOrder('sell');
                }
            }

            form.requestSubmit ? form.requestSubmit() : form.submit();
        };

        this._submitHandler = (e) => {
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) return;
            e.preventDefault();

            if (!form.checkValidity()) {
                form.reportValidity?.();
                return;
            }

            this.mergeStateFromForm(form);
            this.nextStep();
        };

        this.quizContainer.addEventListener('click', this._navHandler);
        this.quizContainer.addEventListener('click', this._valueHandler);
        this.quizContainer.addEventListener('submit', this._submitHandler);
    }

    bindAutocompleteEventsOnce() {
        if (this._acHandler) {
            this.shadowRoot.removeEventListener('autocomplete:select', this._acHandler);
        }

        this._acHandler = (e) => {
            const { value, target } = e.detail || {};
            if (!target) return;

            this.state = this.state || {};

            this.state[target] = value;

            this.nextStep();
        };

        this.shadowRoot.addEventListener('autocomplete:select', this._acHandler);
    }

    renderForm() {
        const id = this.currentStepId();
        if (id === 'choose-track') {
            this.stepQueue = [];
            this.stepQueue.push(...INTRO_STEPS, 'choose-track');
        }

        this.updateProgressBar()
        if (!id) {
            this.finish();
            return;
        }
        this.bindBuyPriceRange();

        this.quizContainer.innerHTML = TEMPLATES[id] || `<div>Unknown step: ${id}</div>`;
    }

    currentStepId() {
        return this.stepQueue[this.stepIndex];
    }

    nextStep() {
        if (this.stepIndex < this.stepQueue.length - 1) {
            this.stepIndex += 1;
            this.renderForm();
        } else {
            this.submit();
        }
    }

    prevStep() {
        this.stepIndex -= 1;
        this.renderForm();
    }

    mergeStateFromForm(form) {
        const data = this.serializeForm(form);

        if (typeof data.mobilePhone === 'string') {
            data.mobilePhone = helpers.normalizePhone(data.mobilePhone);
        }

        Object.assign(this.state, data);
    }

    mergeStateFromContainer(container) {
        const data = this.serializeInputs(container);
        Object.assign(this.state, data);
    }

    serializeForm(form) {
        const out = {};
        const fd = new FormData(form);
        for (const [key, value] of fd.entries()) {
            if (out.hasOwnProperty(key)) {
                out[key] = Array.isArray(out[key]) ? out[key].concat(value) : [out[key], value];
            } else {
                out[key] = value;
            }
        }
        return out;
    }

    serializeInputs(scopeEl) {
        const out = {};
        const fields = scopeEl.querySelectorAll('input, select, textarea');

        fields.forEach((el) => {
            if (el.disabled) return;
            const name = el.name || el.getAttribute('name');
            if (!name) return;

            if (el instanceof HTMLInputElement) {
                const type = (el.type || '').toLowerCase();
                switch (type) {
                    case 'checkbox': {
                        // For checkbox groups sharing the same name, collect checked values
                        if (!out.hasOwnProperty(name)) out[name] = [];
                        if (el.checked) out[name].push(el.value || 'on');
                        break;
                    }
                    case 'radio': {
                        if (el.checked) out[name] = el.value;
                        break;
                    }
                    case 'number': {
                        const v = el.value.trim();
                        if (v !== '') {
                            const num = Number(v);
                            out[name] = Number.isFinite(num) ? num : v;
                        } else {
                            out[name] = '';
                        }
                        break;
                    }
                    default: {
                        out[name] = el.value;
                    }
                }
            } else if (el instanceof HTMLSelectElement) {
                if (el.multiple) {
                    const vals = Array.from(el.selectedOptions).map((opt) => opt.value);
                    out[name] = vals;
                } else {
                    out[name] = el.value;
                }
            } else if (el instanceof HTMLTextAreaElement) {
                out[name] = el.value;
            }
        });

        return out;
    }

    async submit() {
        this.quizContainer.innerHTML = submission;

        this.state.tenant = helpers.getTenantId();
        if (this.offerId) {
            this.state.offer = this.offerId;
        }
        const url = `https://webhook.site/widget-rf`;

        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(this.state),
            });
            if (!res.ok) {
                const text = await res.text().catch(() => '');
                throw new Error(`POST ${url} failed: ${res.status} ${res.statusText} ${text}`);
            }
        } catch (error) {
            console.error('Fetch error:', error);
        }
    }

    updateProgressBar() {
        // Find the bar only inside your widget’s shadow DOM
        const bar = this.shadowRoot.querySelector('.progress-bar');
        if (!bar) return;

        const total = this.stepQueue?.length || 0;
        const idx = Math.min(Math.max(this.stepIndex, 0), Math.max(total - 1, 0));

        // Guard against empty queue
        const pct = total > 0
            ? Math.round(((idx + 1) / total) * 100)
            : 0;

        // Update CSS width and ARIA
        bar.style.width = `${pct}%`;
        bar.setAttribute('aria-valuenow', String(pct));
        bar.setAttribute('aria-valuemin', '0');
        bar.setAttribute('aria-valuemax', '100');

        // Optional: update a text label if you have one
        const label = this.shadowRoot.querySelector('.progress-label');
        if (label) {
            label.textContent = `Step ${idx + 1} of ${total}`;
        }
    }

    bindBuyPriceRange() {
        // Remove previous bindings if re-attaching
        if (this._priceHandler) {
            this.quizContainer.removeEventListener('input', this._priceHandler);
            this.quizContainer.removeEventListener('change', this._priceHandler);
        }

        this._priceHandler = (e) => {
            const target = e.target;
            if (!(target instanceof HTMLElement)) return;

            const form = target.closest('form');
            if (!form) return;

            const minSlider = form.querySelector('input[name="minPrice"]');
            const maxSlider = form.querySelector('input[name="maxPrice"]');
            const minNum = form.querySelector('input[name="minPriceNumber"]');
            const maxNum = form.querySelector('input[name="maxPriceNumber"]');
            const minLabel = form.querySelector('#minPriceLabel');
            const maxLabel = form.querySelector('#maxPriceLabel');

            if (!minSlider || !maxSlider) return;

            const rangeMin = Number(minSlider.min || 0);
            const rangeMax = Number(maxSlider.max || 0);
            const step = Number(minSlider.step || 1);

            const snap = (v) => Math.round(v / step) * step;

            const getVals = () => ({
                min: Number(minSlider.value),
                max: Number(maxSlider.value),
            });

            const setAll = (minVal, maxVal) => {
                // Enforce order
                let min = Math.min(Math.max(minVal, rangeMin), rangeMax);
                let max = Math.min(Math.max(maxVal, rangeMin), rangeMax);
                if (min > max) {
                    // If min crosses max, pull the other side along depending on which control changed
                    if (target === minSlider || target === minNum) max = min;
                    else min = max;
                }

                min = snap(min);
                max = snap(max);

                minSlider.value = String(min);
                maxSlider.value = String(max);
                if (minNum) minNum.value = String(min);
                if (maxNum) maxNum.value = String(max);
                if (minLabel) minLabel.textContent = moneyFmt.format(min);
                if (maxLabel) maxLabel.textContent = moneyFmt.format(max);
            };

            // Determine which control changed and update accordingly
            if (target === minSlider || target === maxSlider) {
                const { min, max } = getVals();
                setAll(min, max);
            } else if (target === minNum || target === maxNum) {
                const minVal = Number(minNum?.value || minSlider.value);
                const maxVal = Number(maxNum?.value || maxSlider.value);
                setAll(minVal, maxVal);
            } else {
                // Irrelevant input
                return;
            }
        };

        // Listen to both input and change for snappy UI and keyboard edits
        this.quizContainer.addEventListener('input', this._priceHandler);
        this.quizContainer.addEventListener('change', this._priceHandler);

        // Optional: on form submit, copy values into canonical fields and state
        if (!this._priceSubmitHandler) {
            this._priceSubmitHandler = (e) => {
                const form = e.target;
                if (!(form instanceof HTMLFormElement)) return;
                // Ensure min/max constraints before saving
                const minSlider = form.querySelector('input[name="minPrice"]');
                const maxSlider = form.querySelector('input[name="maxPrice"]');
                if (!minSlider || !maxSlider) return;

                let min = Number(minSlider.value);
                let max = Number(maxSlider.value);
                if (min > max) [min, max] = [max, min];

                // Persist to state (raw numbers). If you prefer strings, stringify.
                this.state.minPrice = min;
                this.state.maxPrice = max;
            };
            this.quizContainer.addEventListener('submit', this._priceSubmitHandler, true);
        }
    }
}

if (!customElements.get('registration-form-widget')) {
    customElements.define('registration-form-widget', RegistrationFormWidget);
}

export default RegistrationFormWidget;